import { useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import { View, Text, Button, DropdownField, InputField, CheckboxField, Icon } from "../base";

type JsonSchemaPropertyType = "string" | "number" | "boolean" | "array";
type JsonSchemaArrayItemType = "string" | "number" | "boolean";

type PropertyDefinition = {
  id: string;
  name: string;
  type: JsonSchemaPropertyType;
  required: boolean;
  description: string;
  itemType?: JsonSchemaArrayItemType;
};

type JsonSchemaBuilderProps = {
  value?: Record<string, unknown> | null;
  onChange: (schema: Record<string, unknown> | null) => void;
  className?: string;
};

const PROPERTY_TYPE_OPTIONS = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "array", label: "Array" },
];

const ARRAY_ITEM_TYPE_OPTIONS = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
];

const normalizeSchemaToProperties = (schema?: Record<string, unknown> | null): PropertyDefinition[] => {
  if (!schema || typeof schema !== "object") {
    return [];
  }

  const properties = schema.properties as Record<string, { type?: string; description?: string; items?: { type?: string } }> | undefined;
  const required = Array.isArray(schema.required) ? (schema.required as string[]) : [];

  if (!properties) {
    return [];
  }

  return Object.entries(properties).map(([propertyName, propertySchema]) => ({
    id: uuid(),
    name: propertyName,
    type: (propertySchema?.type as JsonSchemaPropertyType) || "string",
    required: required.includes(propertyName),
    description: typeof propertySchema?.description === "string" ? propertySchema.description : "",
    itemType: propertySchema?.type === "array" ? (propertySchema.items?.type as JsonSchemaArrayItemType) || "string" : undefined,
  }));
};

export const JsonSchemaBuilder = ({ value, onChange, className }: JsonSchemaBuilderProps) => {
  const [properties, setProperties] = useState<PropertyDefinition[]>(() => normalizeSchemaToProperties(value));
  const lastEmittedSchemaRef = useRef<string | null>(null);

  useEffect(() => {
    const incomingSchemaString = value ? JSON.stringify(value) : null;

    if (incomingSchemaString === lastEmittedSchemaRef.current) {
      return;
    }

    setProperties((prevProperties) => {
      const normalized = normalizeSchemaToProperties(value);

      return normalized.map((property) => {
        const existing = prevProperties.find((prev) => prev.name === property.name && prev.type === property.type);

        return existing ? { ...property, id: existing.id } : property;
      });
    });
  }, [value]);

  const emitSchema = (propertyDefinitions: PropertyDefinition[]) => {
    const validProperties = propertyDefinitions.filter((property) => property.name.trim() !== "");

    if (validProperties.length === 0) {
      onChange(null);
      lastEmittedSchemaRef.current = null;
      return;
    }

    const required = validProperties.filter((property) => property.required).map((property) => property.name.trim());

    const schema = {
      type: "object",
      additionalProperties: false,
      properties: validProperties.reduce<Record<string, { type: string; description?: string; items?: { type: string } }>>((acc, property) => {
        const trimmedName = property.name.trim();
        const trimmedDescription = property.description.trim();

        acc[trimmedName] = {
          type: property.type,
          ...(property.type === "array" && property.itemType ? { items: { type: property.itemType } } : {}),
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
        };

        return acc;
      }, {}),
      ...(required.length > 0 ? { required } : {}),
    };

    lastEmittedSchemaRef.current = JSON.stringify(schema);
    onChange(schema);
  };

  const handlePropertyChange = (propertyId: string, key: keyof PropertyDefinition, value: string | boolean) => {
    setProperties((prevProperties) => {
      const updatedProperties = prevProperties.map((property) => {
        if (property.id !== propertyId) {
          return property;
        }

        if (key === "type") {
          const nextType = value as JsonSchemaPropertyType;
          return {
            ...property,
            type: nextType,
            itemType: nextType === "array" ? property.itemType || ("string" as JsonSchemaArrayItemType) : undefined,
          };
        }

        if (key === "itemType") {
          return {
            ...property,
            itemType: value as JsonSchemaArrayItemType,
          };
        }

        if (key === "required") {
          return {
            ...property,
            // V1 all properties are required
            required: Boolean(true),
          };
        }

        return {
          ...property,
          [key]: value,
        } as PropertyDefinition;
      });

      emitSchema(updatedProperties);

      return updatedProperties;
    });
  };

  const handleAddProperty = () => {
    setProperties((prevProperties) => {
      const updatedProperties = [
        ...prevProperties,
        {
          id: uuid(),
          name: "",
          type: "string" as JsonSchemaPropertyType,
          required: true,
          description: "",
          itemType: "string" as JsonSchemaArrayItemType,
        },
      ];

      return updatedProperties;
    });
  };

  const handleRemoveProperty = (propertyId: string) => {
    setProperties((prevProperties) => {
      const updatedProperties = prevProperties.filter((property) => property.id !== propertyId);

      emitSchema(updatedProperties);

      return updatedProperties;
    });
  };

  const hasProperties = properties.length > 0;

  return (
    <View className={className}>
      <View className="flex flex-col gap-3">
        {properties.map((property) => (
          <View
            key={property.id}
            className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-900 space-y-3"
          >
            <View className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
              <View className="flex-1">
                <InputField
                  label="Property Name"
                  placeholder="propertyName"
                  value={property.name}
                  onChange={(value) => handlePropertyChange(property.id, "name", value)}
                />
              </View>
              <View className="w-full md:w-48">
                <DropdownField
                  label="Type"
                  options={PROPERTY_TYPE_OPTIONS}
                  value={property.type}
                  onChange={(value) => handlePropertyChange(property.id, "type", value as JsonSchemaPropertyType)}
                />
              </View>
              {property.type === "array" ? (
                <View className="w-full md:w-48">
                  <DropdownField
                    label="Item Type"
                    options={ARRAY_ITEM_TYPE_OPTIONS}
                    value={(property.itemType || "string") as JsonSchemaArrayItemType}
                    onChange={(value) => handlePropertyChange(property.id, "itemType", value as JsonSchemaArrayItemType)}
                  />
                </View>
              ) : null}
              <Button
                variant="ghost"
                onClick={() => handleRemoveProperty(property.id)}
                className="!py-2 md:mt-6 text-red-500 dark:text-red-300"
              >
                <Icon
                  name="trash"
                  size={16}
                  className="mr-1"
                />
              </Button>
            </View>
            <InputField
              label="Description"
              placeholder="Describe this property"
              value={property.description}
              onChange={(value) => handlePropertyChange(property.id, "description", value)}
            />
          </View>
        ))}

        {!hasProperties && (
          <View className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center">
            <Text className="text-sm text-gray-500 dark:text-gray-400">No properties defined. Add one to start building your schema.</Text>
          </View>
        )}

        <Button
          variant="ghost"
          onClick={handleAddProperty}
          className="self-start"
        >
          <Icon
            name="add"
            size={16}
            className="mr-1"
          />
          Add Property
        </Button>

        {/* Preview intentionally removed per UX request */}
      </View>
    </View>
  );
};
